import React, { useState } from 'react';
import { APIContact } from '../helper/APIFunctions';

/**
 * Report Inaccuracies Button Component
 * 
 * A reusable button that allows users to report AI inaccuracies.
 * Can be displayed as an icon button, text button, or floating button.
 * 
 * @param {Object} props
 * @param {string} props.context - The context/page where the report is coming from (e.g., 'scrollytelling', 'tour', 'organ-segmentation')
 * @param {string} props.variant - Display variant: 'icon' (default), 'text', 'floating'
 * @param {Object} props.style - Additional inline styles for the button
 * @param {string} props.className - Additional CSS classes for the button
 */
const ReportInaccuraciesButton = ({ 
  context = 'general', 
  variant = 'icon',
  style = {},
  className = ''
}) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportStatus, setReportStatus] = useState({});
  const [hoveredTooltip, setHoveredTooltip] = useState(false);

  const handleSubmitReport = async () => {
    setReportSubmitting(true);
    setReportStatus({});

    // Compose email body with context
    const emailBody = `Context: ${context}\n\nUser Comments:\n${reportComment || 'No additional comments provided'}\n\n========================================\nREPORT DETAILS:\n========================================\nPage URL: ${window.location.href}\nTimestamp: ${new Date().toISOString()}\nUser Agent: ${navigator.userAgent}`;

    APIContact(
      {
        email: 'support@snorkle.io',
        comment: emailBody,
        header: `AI Inaccuracy Report - ${context}`
      },
      () => {
        setReportSubmitting(false);
        setReportStatus({ success: true });
        setTimeout(() => {
          setShowReportModal(false);
          setReportComment('');
          setReportStatus({});
        }, 2500);
      },
      (err) => {
        setReportSubmitting(false);
        setReportStatus({ error: typeof err === 'string' ? err : 'Failed to submit report. Please try again.' });
      }
    );
  };

  // Button styles based on variant
  const getButtonStyle = () => {
    const baseStyle = {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      transition: 'color 200ms',
      ...style
    };

    if (variant === 'floating') {
      return {
        ...baseStyle,
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 100,
        padding: '10px',
        background: 'rgba(239, 68, 68, 0.9)',
        borderRadius: '50%',
        width: '48px',
        height: '48px',
        justifyContent: 'center',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        color: '#fff',
        fontSize: '20px'
      };
    }

    if (variant === 'text') {
      return {
        ...baseStyle,
        padding: '8px 16px',
        color: hoveredTooltip ? '#ef4444' : '#6b7280',
        fontSize: '14px',
        gap: '8px'
      };
    }

    // Default icon variant
    return {
      ...baseStyle,
      padding: '2px',
      color: hoveredTooltip ? '#ef4444' : '#6b7280',
      fontSize: '16px'
    };
  };

  return (
    <>
      {/* Report Button */}
      <div style={{ position: 'relative', display: 'inline-block' }} className={className}>
        <button
          onClick={() => setShowReportModal(true)}
          style={getButtonStyle()}
          onMouseEnter={() => setHoveredTooltip(true)}
          onMouseLeave={() => setHoveredTooltip(false)}
          title="Report AI Inaccuracies"
        >
          <span>⚠</span>
          {variant === 'text' && <span>Report AI Inaccuracies</span>}
        </button>
        
        {/* Tooltip (only for icon variant) */}
        {variant === 'icon' && hoveredTooltip && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: 6,
            padding: '6px 10px',
            background: 'rgba(17, 24, 39, 0.95)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 6,
            color: '#e5e9f5',
            fontSize: 12,
            whiteSpace: 'nowrap',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
          }}>
            Report AI Inaccuracies
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 10,
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid rgba(239, 68, 68, 0.3)'
            }} />
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }} 
          onClick={() => !reportSubmitting && setShowReportModal(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              borderRadius: 16,
              border: '1px solid rgba(239, 68, 68, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
              maxWidth: 700,
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#f0f4ff', 
                fontSize: 18, 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10 
              }}>
                <span>🚩</span>
                <span>Report AI Inaccuracies</span>
              </h3>
              <button
                onClick={() => !reportSubmitting && setShowReportModal(false)}
                disabled={reportSubmitting}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  background: 'rgba(30, 32, 42, 0.6)',
                  color: '#fca5a5',
                  fontSize: 20,
                  cursor: reportSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: reportSubmitting ? 0.5 : 1
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 24px', overflow: 'auto', flex: 1 }}>
              {reportStatus.success ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                  <h4 style={{ color: '#f0f4ff', marginBottom: 12 }}>Report Submitted</h4>
                  <p style={{ color: '#9ca3af', fontSize: 14 }}>
                    Thank you for your feedback. We'll review your report and address any issues.
                  </p>
                </div>
              ) : (
                <>
                  {/* Privacy Notice */}
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 8,
                    marginBottom: 20
                  }}>
                    <p style={{ margin: 0, color: '#fca5a5', fontSize: 13, lineHeight: 1.6 }}>
                      <strong>🔒 Privacy Notice:</strong> Your feedback will help us improve AI accuracy. 
                      Page context and your comments will be submitted anonymously.
                    </p>
                  </div>

                  {/* Comment Input */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      color: '#e5e9f5',
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8
                    }}>
                      Describe the Issue
                    </label>
                    <textarea
                      value={reportComment}
                      onChange={(e) => setReportComment(e.target.value)}
                      placeholder="Please describe the inaccuracy or issue you encountered..."
                      disabled={reportSubmitting}
                      style={{
                        width: '100%',
                        minHeight: 120,
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(0, 0, 0, 0.3)',
                        color: '#e5e9f5',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Error Display */}
                  {reportStatus.error && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                      borderRadius: 8,
                      color: '#fca5a5',
                      fontSize: 13,
                      marginBottom: 20
                    }}>
                      <strong>Error:</strong> {reportStatus.error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowReportModal(false)}
                      disabled={reportSubmitting}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(30, 32, 42, 0.6)',
                        color: '#e5e9f5',
                        cursor: reportSubmitting ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        opacity: reportSubmitting ? 0.5 : 1
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitReport}
                      disabled={reportSubmitting || !reportComment.trim()}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        background: reportSubmitting || !reportComment.trim() ? 'rgba(100, 100, 100, 0.5)' : 'rgba(239, 68, 68, 0.2)',
                        color: reportSubmitting || !reportComment.trim() ? '#9ca3af' : '#ef4444',
                        cursor: reportSubmitting || !reportComment.trim() ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        fontWeight: 600
                      }}
                    >
                      {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportInaccuraciesButton;

